// One rule for showing a category, and one for finding a category from text.
//
// A category used to have two names: a stored `name` in DEFRA workbook wording ("Waste disposal")
// and a displayed `friendlyName` ("Waste Management"). Screens disagreed on which to show. The
// backend now stores the display name in `name` and keeps the old one in `legacyName`, and
// `friendlyName` is kept equal to `name` until it is dropped. Every screen shows a category through
// getCategoryDisplayName, so this works whichever of the frontend and backend deploys first, and
// dropping `friendlyName` later is a change to this one function.

interface CategoryNames {
  name?: string | null;
  friendlyName?: string | null;
  legacyName?: string | null;
}

/** The name to show for a category. Empty string when there is none. */
export const getCategoryDisplayName = (category?: CategoryNames | null): string =>
  (category?.friendlyName || category?.name || '').trim();

const normalise = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Finds a category from text that names it: a rule's stored `categoryName`, an import line, a factor
 * workbook cell. Mirrors the backend lookup (lib/category/resolveCategoryByText): stored name, then
 * display name, then the name it used to have, ignoring case and spacing. The stored name wins a
 * tie, so one category's display or old name can never shadow another category's real name.
 */
export function findCategoryByText<T extends CategoryNames>(categories: T[] | undefined | null, text: unknown): T | undefined {
  const key = normalise(text);
  if (!categories || key === '') return undefined;
  return categories.find(category => normalise(category.name) === key)
    ?? categories.find(category => normalise(category.friendlyName) === key)
    ?? categories.find(category => normalise(category.legacyName) === key);
}

const UNGROUPED_LABEL = 'Other';

/** The heading a category sits under in a grouped list: its category group, or "Other". */
export const getCategoryGroupLabel = (category?: { friendlyCategoryGroup?: { name: string } | null } | null): string =>
  category?.friendlyCategoryGroup?.name || UNGROUPED_LABEL;

/**
 * Categories ordered for a list grouped by category group: groups in their display order (ungrouped
 * last), categories by display name inside each. MUI Autocomplete's groupBy needs options already
 * ordered by group. Returns a new array.
 */
export const sortCategoriesByGroup = <T extends CategoryNames & { friendlyCategoryGroup?: { name: string; displayOrder: number } | null }>(categories: T[]): T[] =>
  categories.slice().sort((a, b) => {
    const orderA = a.friendlyCategoryGroup ? a.friendlyCategoryGroup.displayOrder : Number.MAX_SAFE_INTEGER;
    const orderB = b.friendlyCategoryGroup ? b.friendlyCategoryGroup.displayOrder : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    const byGroup = getCategoryGroupLabel(a).localeCompare(getCategoryGroupLabel(b));
    if (byGroup !== 0) return byGroup;
    return getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b));
  });

/** The distinct category groups present on a category list, in their display order. */
export const getCategoryGroups = (
  categories?: Array<{ friendlyCategoryGroup?: { id: string; name: string; displayOrder: number } | null }> | null,
): Array<{ id: string; name: string; displayOrder: number }> => {
  const byId = new Map<string, { id: string; name: string; displayOrder: number }>();
  (categories || []).forEach(category => {
    const group = category.friendlyCategoryGroup;
    if (group && !byId.has(group.id)) byId.set(group.id, group);
  });
  return Array.from(byId.values()).sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
};

/** Sorts categories by the name users see. Returns a new array. */
export const sortCategoriesByDisplayName = <T extends CategoryNames>(categories: T[]): T[] =>
  categories.slice().sort((a, b) => getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b)));
